import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import { InlineSpinner } from "components/base/Spinner";
import { useContext } from "react";
import { v4 } from "uuid";
import { FETCH_ODOO_PRODUCT_COST } from "../graphql";
import { computeOdooProductCostInCurrency } from "../utils";
import { OrderContext } from "./OrderDetail";
import OrderLineView from "./OrderLineView";

const ProductsView = ({ odooPricelists }) => {
  const { originalOrder, order, setOrder, validStocks } =
    useContext(OrderContext);

  const productOdooIds = order.orderLines
    .filter((i) => i.product)
    .map((i) => i.product.odooId)
    .filter((i) => i);

  const fetchProductOdooCost = useQuery(FETCH_ODOO_PRODUCT_COST, {
    variables: {
      instance: "AB",
      ids: productOdooIds,
    },
  });

  const odooProducts = computeOdooProductCostInCurrency(
    fetchProductOdooCost.data,
    order.pricelist?.currency,
  );

  const availableProducts = validStocks.map((stock) => {
    return {
      ...stock.product,
      name: `[${stock.product.number}] ${stock.product.name}`,
      stock,
    };
  });

  function addLine() {
    setOrder((prev) => ({
      ...prev,
      orderLines: [
        ...prev.orderLines,
        {
          id: `new-${v4()}`,
          isNew: true,
          product: null,
          qty: 1,
          salesPrice: 0,
          discountPercentage: 0,
        },
      ],
    }));
  }

  function handleLineSelectorBlur(lineId) {
    setTimeout(() => {
      setOrder((prev) => {
        const line = prev.orderLines.find((i) => i.id === lineId);
        if (!line) return prev;
        if (line.product?.id || line.productId) return prev;
        return {
          ...prev,
          orderLines: prev.orderLines.filter((i) => i.id !== lineId),
        };
      });
    }, 0);
  }

  const odooPricelist = odooPricelists.find(
    (i) => i.id === order.odooPricelistId,
  );

  const odooTotal = order.orderLines.reduce(
    (acc, line) =>
      acc +
      parseInt(line.qty) *
        parseFloat(line.salesPrice) *
        ((100 - parseFloat(line.discountPercentage)) / 100),
    0,
  );

  const odooCurrency = odooPricelist ? odooPricelist.currency : null;
  const odooCurrencyTotal = odooCurrency
    ? order.orderLines.reduce(
        (acc, line) =>
          acc +
          parseInt(line.qty) *
            parseFloat(line.salesPrice) *
            ((100 - parseFloat(line.discountPercentage)) / 100) *
            order.exchangeRate,
        0,
      )
    : null;

  const totalCost = order.orderLines.reduce((acc, line) => {
    const productOdooId = line.product?.odooId;
    if (!productOdooId) return acc;
    const productCost = odooProducts[productOdooId] || 0;
    return acc + parseInt(line.qty) * productCost;
  }, 0);

  const totalMargin = odooTotal - totalCost;
  const totalMarginPercentage =
    odooTotal > 0 ? (totalMargin / odooTotal) * 100 : 0;

  return (
    <div className="card">
      <h5>Products</h5>

      {originalOrder.stockCategory ? (
        <div className="mt-2 -mx-2">
          <table>
            <thead className="whitespace-nowrap">
              <tr>
                <th>Item</th>
                <th className="text-right">Stock</th>
                <th className="text-right">Quantity</th>
                <th className="text-right">Sales Price</th>
                <th className="text-right">Disc %</th>
                <th className="text-right">Disc Price</th>
                <th className="text-right">
                  <div className="flex items-center justify-end space-x-2">
                    {fetchProductOdooCost.loading && (
                      <InlineSpinner size={14} />
                    )}
                    <span>Margin</span>
                  </div>
                </th>
                <th className="text-right">Subtotal</th>
                {odooCurrency && (
                  <>
                    <th className="text-right">Sales Price({odooCurrency})</th>
                    <th className="text-right">Subtotal({odooCurrency})</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {order.orderLines.map((line) => (
                <OrderLineView
                  line={line}
                  key={line.id}
                  availableProducts={availableProducts}
                  odooCurrency={odooCurrency}
                  autoFocus={line.isNew && !line.product && !line.productId}
                  onLineSelectorBlur={() => handleLineSelectorBlur(line.id)}
                  odooProducts={odooProducts}
                />
              ))}
              <tr>
                <td>
                  <Button onClick={addLine}>+ Add Product</Button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="font-semibold border-y border-gray-100 dark:border-gray-700">
                <td>Total</td>
                <td></td>
                <td className="text-right">
                  {order.orderLines
                    .reduce((acc, line) => acc + parseInt(line.qty), 0)
                    .toLocaleString(undefined)}
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td className="text-right whitespace-nowrap">
                  {totalMargin.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  (
                  {totalMarginPercentage.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  %)
                </td>
                <td className="text-right">
                  {odooTotal.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                {odooCurrency && (
                  <>
                    <td></td>
                    <td className="text-right">
                      {odooCurrencyTotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="mt-2 opacity-70">
          Choose a stock category to add products.
        </div>
      )}
    </div>
  );
};

export default ProductsView;
